/**
 * Swipe to go back - plan_20 (Money plan theme).
 *
 * Put your thumb on the left edge of the screen and drag right: the page
 * follows your finger with the real page before it underneath. Let go past
 * 28% of the screen and it slides away, otherwise it springs back.
 *
 * Each swipeable page is marked data-swipe-layer. A swipe only starts on the
 * page that's actually on top at your thumb, and never while a pop-up is open
 * over it, so pages stacked on each other never fight over the same touch.
 */
import { useEffect, useRef } from 'react';

// w2_22: set the slide as !important, so a page whose own styles fix its
// position (like Settings) still follows your finger. '' takes it off again.
const put = (el, prop, val) => {
  if (!el) return;
  if (val === '') el.style.removeProperty(prop);
  else el.style.setProperty(prop, val, 'important');
};

export function useSwipeBack(getEl, onBack, enabled = true) {
  const latest = useRef({ getEl, onBack, enabled });
  latest.current = { getEl, onBack, enabled };
  useEffect(() => {
    let on = false; let sx = 0; let sy = 0; let dx = 0; let el = null;
    const reset = () => { if (el) { put(el, 'transition', ''); put(el, 'transform', ''); } on = false; el = null; };
    const start = (e) => {
      const L = latest.current;
      if (!L.enabled) return;
      const t = e.touches && e.touches[0];
      if (!t || t.clientX > 30) return; // the left edge only
      const layer = L.getEl && L.getEl();
      if (!layer) return;
      const hit = document.elementFromPoint(Math.max(1, t.clientX), t.clientY);
      if (!hit || !layer.contains(hit)) return;
      if (hit.closest('[data-swipe-layer]') !== layer) return; // another page is on top
      for (let n = hit; n && n !== layer; n = n.parentElement) {
        if (window.getComputedStyle(n).position === 'fixed') return; // a pop-up is open
      }
      el = layer;
      on = true; sx = t.clientX; sy = t.clientY; dx = 0;
      put(el, 'transition', 'none');
    };
    const move = (e) => {
      if (!on) return;
      const t = e.touches[0];
      dx = Math.max(0, t.clientX - sx);
      if (Math.abs(t.clientY - sy) > 40 && dx < 20) { reset(); return; } // it was a scroll
      put(el, 'transform', 'translateX(' + dx + 'px)');
      e.preventDefault();
    };
    const end = () => {
      if (!on) return;
      on = false;
      const page = el;
      el = null;
      put(page, 'transition', 'transform .2s ease');
      if (dx > window.innerWidth * 0.28) {
        put(page, 'transform', 'translateX(100%)');
        setTimeout(() => {
          const L = latest.current;
          if (L.onBack) L.onBack();
          // If the page is still there afterwards, bring it back into place.
          setTimeout(() => { if (page.isConnected) { put(page, 'transition', ''); put(page, 'transform', ''); } }, 400);
        }, 180);
      } else { put(page, 'transform', ''); setTimeout(() => put(page, 'transition', ''), 220); }
    };
    document.addEventListener('touchstart', start, { passive: true });
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', end);
    document.addEventListener('touchcancel', reset);
    return () => {
      document.removeEventListener('touchstart', start);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', end);
      document.removeEventListener('touchcancel', reset);
    };
  }, []);
}
